import { FontStyles } from "../../utils/font-styles.ts";
import { useEffect, useRef } from "react";
import { useLoginConfig } from "../../user-auth/utils/login-config-context.tsx";
import { loadGoogleScript } from "../../user-auth/utils/google-script-loader.ts";
import { socialLogin } from "../../user-auth/utils/social-login.ts";
import { SocialButtonsProps } from "./social-buttons-props.ts";

const baseStyle =
  "flex justify-center items-center py-2.5 bg-cloud rounded border-2 border-neutral-200 font-semibold text-slate w-full hover:bg-neutral-white focus:shadow-[0_0_0_4px] focus:outline-none focus:neutral-white focus:shadow-neutral-100 active:!shadow-neutral-200 " +
  FontStyles["body-md"];

export const GoogleButton: React.FC<SocialButtonsProps> = ({ onAuthSuccess }) => {
  const loginConfig = useLoginConfig();

  // ref to hold the promise that loads the google script
  const googleScriptLoadingPromise = useRef<Promise<void> | null>(null);

  useEffect(() => {
    async function loadScript() {
      if (!loginConfig) return;
      try {
        googleScriptLoadingPromise.current = loadGoogleScript();
        await googleScriptLoadingPromise.current;

        if (typeof window.gapi !== "undefined") {
          window.gapi.load("auth2", initSignin);
        }
      } catch (error) {
        console.error("Error loading Google script", error);
      }
    }

    void loadScript();
  }, []);

  function initSignin() {
    if (!loginConfig) return;
    if (typeof window.gapi.auth2 !== "undefined") {
      void gapi.auth2.init({
        client_id: loginConfig.googleClientId,
        scope: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile",
      });
    } else {
      throw Error("Google auth init error. Script not loaded?");
    }
  }
  async function GoogleLogin() {
    if (!loginConfig) return;
    if (googleScriptLoadingPromise.current === null) {
      throw new Error("Google script didn't start loading. Did you forget to call loadGoogleScript?");
    }

    if (typeof window.gapi !== "undefined") {
      const auth2 = window.gapi.auth2.getAuthInstance();
      // Sign the user in, and then retrieve their ID.
      const user = await auth2.signIn();
      const profile = user.getBasicProfile();
      const token: string = user.getAuthResponse().id_token;
      const requestBody: { name: string; email: string; token: string; scope: string } = {
        name: profile.getName(),
        email: profile.getEmail(),
        token: token,
        scope: "Google",
      };
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      };
      const responseStatus = await socialLogin(requestOptions, "google");
      await onAuthSuccess?.(responseStatus);
    }
  }

  return (
    <button onClick={() => void GoogleLogin()} className={baseStyle}>
      <img className="h-[18px]" src="/static/icons/google-social-icon.png" alt="Google social icon" />
      <span className="leading-[14px] hidden md:block md:pl-2 ">Google</span>
    </button>
  );
};
